/**
 * GoalFilters Component
 * Filter controls for goal lists
 * BP-06-03: Care Team & Goals Frontend
 */

import React from 'react';
import { Filter, X, Search } from 'lucide-react';
import {
  GoalFilters as GoalFiltersType,
  GoalType,
  GoalStatus,
  GoalPriority,
  GOAL_TYPE_LABELS,
  GOAL_STATUS_LABELS,
  PRIORITY_LABELS,
  GOAL_CATEGORIES,
} from '@/features/goals/types/goalsTypes';

interface GoalFiltersProps {
  filters: GoalFiltersType;
  onChange: (filters: GoalFiltersType) => void;
  onClear: () => void;
  showIepFilter?: boolean;
}

export function GoalFilters({
  filters,
  onChange,
  onClear,
  showIepFilter = true,
}: GoalFiltersProps) {
  const hasActiveFilters = Object.values(filters).some(
    (v) => v !== undefined && v !== '' && v !== null
  );

  const updateFilter = <K extends keyof GoalFiltersType>(
    key: K,
    value: GoalFiltersType[K]
  ) => {
    onChange({ ...filters, [key]: value || undefined });
  };

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
        <input
          type="text"
          value={filters.search || ''}
          onChange={(e) => updateFilter('search', e.target.value)}
          placeholder="Search goals..."
          className="w-full pl-10 pr-4 py-2.5 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage transition-colors"
        />
      </div>

      {/* Filter Row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-sm text-warm-gray">
          <Filter className="w-4 h-4" />
          <span>Filters:</span>
        </div>

        {/* Goal Type */}
        <select
          value={filters.goalType || ''}
          onChange={(e) => updateFilter('goalType', e.target.value as GoalType)}
          className="px-3 py-1.5 bg-cream border-2 border-transparent rounded-lg text-sm focus:outline-none focus:border-sage transition-colors"
        >
          <option value="">All Types</option>
          {Object.entries(GOAL_TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        {/* Status */}
        <select
          value={filters.status || ''}
          onChange={(e) => updateFilter('status', e.target.value as GoalStatus)}
          className="px-3 py-1.5 bg-cream border-2 border-transparent rounded-lg text-sm focus:outline-none focus:border-sage transition-colors"
        >
          <option value="">All Statuses</option>
          {Object.entries(GOAL_STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        {/* Priority */}
        <select
          value={filters.priority || ''}
          onChange={(e) => updateFilter('priority', e.target.value as GoalPriority)}
          className="px-3 py-1.5 bg-cream border-2 border-transparent rounded-lg text-sm focus:outline-none focus:border-sage transition-colors"
        >
          <option value="">All Priorities</option>
          {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        {/* Category */}
        <select
          value={filters.category || ''}
          onChange={(e) => updateFilter('category', e.target.value)}
          className="px-3 py-1.5 bg-cream border-2 border-transparent rounded-lg text-sm focus:outline-none focus:border-sage transition-colors"
        >
          <option value="">All Categories</option>
          {GOAL_CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>

        {/* IEP Only Toggle */}
        {showIepFilter && (
          <label className="flex items-center gap-2 px-3 py-1.5 bg-cream rounded-lg cursor-pointer hover:bg-cream-dark transition-colors">
            <input
              type="checkbox"
              checked={filters.isIepGoal === true}
              onChange={(e) => updateFilter('isIepGoal', e.target.checked ? true : undefined)}
              className="w-4 h-4 rounded border-warm-gray text-sage focus:ring-sage"
            />
            <span className="text-sm text-navy">IEP Goals Only</span>
          </label>
        )}

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            onClick={onClear}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-terracotta hover:bg-terracotta/10 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
            Clear All
          </button>
        )}
      </div>

      {/* Active Filter Pills */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2">
          {filters.goalType && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-sage/10 text-sage-dark text-xs rounded-full">
              {GOAL_TYPE_LABELS[filters.goalType]}
              <button onClick={() => updateFilter('goalType', undefined)} className="hover:text-sage">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {filters.status && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-sage/10 text-sage-dark text-xs rounded-full">
              {GOAL_STATUS_LABELS[filters.status]}
              <button onClick={() => updateFilter('status', undefined)} className="hover:text-sage">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {filters.priority && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-sage/10 text-sage-dark text-xs rounded-full">
              {PRIORITY_LABELS[filters.priority]}
              <button onClick={() => updateFilter('priority', undefined)} className="hover:text-sage">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {filters.category && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-sage/10 text-sage-dark text-xs rounded-full">
              {filters.category}
              <button onClick={() => updateFilter('category', undefined)} className="hover:text-sage">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {filters.isIepGoal && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-navy/10 text-navy text-xs rounded-full">
              IEP Goals
              <button onClick={() => updateFilter('isIepGoal', undefined)} className="hover:text-navy-light">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default GoalFilters;

